import { Typography, Box, styled, Stack } from "@mui/material";
import { basicColours, mainColours } from "@qubit/autoparts";

import { useAppSelector } from "~/app/store";
import useFlag from "~/config/flags";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { PickingStateV2TotePick } from "~/types/api";

type Props = {
  allPicksForTote: PickingStateV2TotePick[];
  hasExternalToteId: boolean;
};

const Container = styled(Stack, {
  shouldForwardProp: (prop) =>
    ![
      "ap_fusionPortScreenEnabled",
      "hasExternalToteId",
      "enableColorChanges"
    ].includes(prop as string)
})<{
  ap_fusionPortScreenEnabled: boolean;
  hasExternalToteId: boolean;
  enableColorChanges: boolean;
}>(({ ap_fusionPortScreenEnabled, hasExternalToteId, enableColorChanges }) => ({
  alignItems: "center",
  minWidth: "max-content",
  marginLeft: 10,
  ...(enableColorChanges
    ? {
        position: "absolute",
        left: "100%",
        top: 0,
        height: "100%"
      }
    : {
        justifyContent: "center",
        alignSelf: ap_fusionPortScreenEnabled
          ? "unset"
          : hasExternalToteId
            ? "anchor-center"
            : "flex-end",
        height: ap_fusionPortScreenEnabled ? 240 : 252
      })
}));

const ProgressBarTrack = styled(Box, {
  shouldForwardProp: (prop) =>
    !["ap_fusionPortScreenEnabled", "enableColorChanges"].includes(
      prop as string
    )
})<{
  ap_fusionPortScreenEnabled: boolean;
  enableColorChanges: boolean;
}>(({ ap_fusionPortScreenEnabled, enableColorChanges, theme }) => ({
  display: "flex",
  position: "relative",
  width: 20,
  ...(enableColorChanges
    ? { flexGrow: 1, minHeight: 0 }
    : { height: ap_fusionPortScreenEnabled ? 120 : 252 }),
  borderRadius: 10,
  backgroundColor: enableColorChanges
    ? theme.palette.autostoreDesignSystem.surface.interactive.muted.default
    : basicColours.mono[200]
}));

const ProgressBarFill = styled("span", {
  shouldForwardProp: (prop) =>
    !["percent", "enableColorChanges", "isComplete"].includes(prop as string)
})<{
  percent: number;
  enableColorChanges: boolean;
  isComplete: boolean;
}>(({ percent, enableColorChanges, isComplete, theme }) => ({
  position: "absolute",
  bottom: 0,
  width: 20,
  height: `${percent}%`,
  borderRadius: 10,
  backgroundColor: enableColorChanges
    ? isComplete
      ? theme.palette.autostoreDesignSystem.surface.interactive.accent.disabled
      : theme.palette.autostoreDesignSystem.surface.interactive.accent.default
    : mainColours.infographic[1],
  transition: "height 0.5s ease-in-out"
}));

const ToteProgressBarV2 = ({ allPicksForTote, hasExternalToteId }: Props) => {
  const { ap_fusionPortScreenEnabled } = useAppSelector(selectServerConfig);
  const { enableColorChanges } = useFlag();

  const totalPicks = allPicksForTote.reduce(
    (acc, curr) =>
      curr.canceledReason || curr.canceledTimestamp ? acc : acc + curr.quantity,
    0
  );
  const completedPicks = allPicksForTote.reduce(
    (acc, curr) => (curr.completedTimestamp ? acc + curr.quantity : acc),
    0
  );
  const percentComplete =
    totalPicks === 0 ? 0 : Math.min((completedPicks / totalPicks) * 100, 100);
  const isComplete = totalPicks > 0 && completedPicks >= totalPicks;

  return (
    <Container
      ap_fusionPortScreenEnabled={ap_fusionPortScreenEnabled}
      hasExternalToteId={hasExternalToteId}
      enableColorChanges={enableColorChanges}
    >
      <ProgressBarTrack
        ap_fusionPortScreenEnabled={ap_fusionPortScreenEnabled}
        enableColorChanges={enableColorChanges}
        aria-label="progress-bar-track"
      >
        <ProgressBarFill
          percent={percentComplete}
          enableColorChanges={enableColorChanges}
          isComplete={isComplete}
          aria-label="progress-bar-fill"
        />
      </ProgressBarTrack>

      <Typography
        variant="h6"
        color={basicColours.mono.black}
        fontWeight={500}
        sx={{ opacity: "60%", ...(enableColorChanges && { lineHeight: 1 }) }}
      >
        {`${completedPicks} / ${totalPicks}`}
      </Typography>
    </Container>
  );
};

export default ToteProgressBarV2;
